import pdfMake from "pdfmake/build/pdfmake.min";
import { pdfMake as pdfFonts } from "pdfmake/build/vfs_fonts";

pdfMake.vfs = pdfFonts.vfs;

const amount = (value) => Number(value || 0);
const money = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(amount(value));
const total = (rows, field) =>
  rows.reduce((sum, row) => sum + amount(row[field]), 0);
export function downloadDepartmentFinancialReport({ business, departments }) {
  const rows = [...departments].sort((left, right) =>
    String(left.name || "").localeCompare(String(right.name || "")),
  );
  const totals = {
    students: total(rows, "studentCount"),
    expected: total(rows, "totalFee"),
    parent: total(rows, "totalParentPay"),
    charity: total(rows, "totalCharity"),
    zakat: total(rows, "totalZakat"),
    payroll: total(rows, "totalPayrollDeduction"),
    discount: total(rows, "totalDiscount"),
    expense: total(rows, "totalExpense"),
  };
  totals.net = totals.expected - totals.discount - totals.expense;
  const organization = business && business.name ? business.name : "Taleem";
  const generatedAt = new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date());
  const filename = `${organization.replace(/[^a-z0-9]+/gi, "-")}-department-financial-report.pdf`;
  const header = [
    "Department", "Monthly fee", "Students", "Expected fees", "Parent paying",
    "Charity", "Zakat", "Payroll", "Discount", "Expense", "Surplus / shortfall",
  ].map((text) => ({ text, style: "tableHeader" }));
  const body = [
    header,
    ...rows.map((row) => [
      { text: row.name || "Unnamed department", bold: true },
      { text: money(row.defaultFee), alignment: "right" },
      { text: String(amount(row.studentCount)), alignment: "center" },
      { text: money(row.totalFee), alignment: "right" },
      { text: money(row.totalParentPay), alignment: "right", color: "#2f8738" },
      { text: money(row.totalCharity), alignment: "right" },
      { text: money(row.totalZakat), alignment: "right", color: "#8e6c16" },
      { text: money(row.totalPayrollDeduction), alignment: "right", color: "#5e35b1" },
      { text: money(row.totalDiscount), alignment: "right" },
      { text: money(row.totalExpense), alignment: "right" },
      { text: money(amount(row.totalFee) - amount(row.totalDiscount) - amount(row.totalExpense)), alignment: "right", color: amount(row.totalFee) - amount(row.totalDiscount) - amount(row.totalExpense) < 0 ? "#c62828" : "#2f8738" },
    ]),
    [
      { text: "TOTAL", bold: true }, "",
      { text: String(totals.students), alignment: "center", bold: true },
      { text: money(totals.expected), alignment: "right", bold: true },
      { text: money(totals.parent), alignment: "right", bold: true },
      { text: money(totals.charity), alignment: "right", bold: true },
      { text: money(totals.zakat), alignment: "right", bold: true },
      { text: money(totals.payroll), alignment: "right", bold: true },
      { text: money(totals.discount), alignment: "right", bold: true },
      { text: money(totals.expense), alignment: "right", bold: true },
      { text: money(totals.net), alignment: "right", bold: true },
    ],
  ];
  const definition = {
    pageSize: "LETTER",
    pageOrientation: "landscape",
    pageMargins: [20, 30, 20, 36],
    defaultStyle: { font: "Roboto", fontSize: 7, color: "#223047" },
    footer(currentPage, pageCount) {
      return {
        columns: [
          { text: organization, margin: [20, 0, 0, 0], color: "#77837b", fontSize: 7 },
          { text: `Page ${currentPage} of ${pageCount}`, alignment: "right", margin: [0, 0, 20, 0], color: "#77837b", fontSize: 7 },
        ],
      };
    },
    content: [
      { text: organization, color: "#3f9f48", bold: true, fontSize: 11 },
      { text: "DEPARTMENT FINANCIAL REPORT", bold: true, fontSize: 19, margin: [0, 3, 0, 2] },
      { text: `Generated ${generatedAt}`, color: "#6f7c74", fontSize: 8, margin: [0, 0, 0, 14] },
      {
        columns: [
          { text: [{ text: `${rows.length}`, style: "metricValue" }, "\nDepartments"], style: "metric" },
          { text: [{ text: `${totals.students}`, style: "metricValue" }, "\nActive students"], style: "metric" },
          { text: [{ text: money(totals.expected), style: "metricValue" }, "\nExpected fees"], style: "metric" },
          { text: [{ text: money(totals.discount), style: "metricValue" }, "\nDiscounts"], style: "metric" },
        ],
        columnGap: 8,
        margin: [0, 0, 0, 14],
      },
      {
        table: {
          headerRows: 1,
          dontBreakRows: true,
          widths: [104, 46, 34, 56, 56, 46, 42, 46, 46, 50, 62],
          body,
        },
        layout: {
          fillColor(rowIndex) {
            if (rowIndex === 0) return "#3f9f48";
            if (rowIndex === body.length - 1) return "#e8f4e9";
            return rowIndex % 2 === 0 ? "#f5f8f5" : null;
          },
          hLineColor: "#d9e2db",
          vLineColor: "#e4ebe5",
          paddingLeft: () => 3,
          paddingRight: () => 3,
          paddingTop: () => 5,
          paddingBottom: () => 5,
        },
      },
    ],
    styles: {
      tableHeader: { bold: true, color: "#ffffff", fontSize: 6.5, alignment: "center", noWrap: false },
      metric: { color: "#6f7c74", fontSize: 7.5, margin: [8, 7, 8, 7] },
      metricValue: { bold: true, color: "#223047", fontSize: 12 },
    },
  };

  return new Promise((resolve, reject) => {
    try {
      pdfMake.createPdf(definition).download(filename, resolve);
    } catch (error) {
      reject(error);
    }
  });
}
